"use client";

import { createFrameThumbnailer, type FrameThumbnailer } from "mediatools";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { formatClock } from "./format";

const DEBOUNCE_MS = 200;
const THUMB_WIDTH = 240;

/** Copy a decoded frame (HTML or Offscreen canvas) into a JPEG data URL. */
function toDataUrl(canvas: HTMLCanvasElement | OffscreenCanvas): string | null {
  const tmp = document.createElement("canvas");
  tmp.width = canvas.width;
  tmp.height = canvas.height;
  const ctx = tmp.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(canvas as CanvasImageSource, 0, 0);
  return tmp.toDataURL("image/jpeg", 0.7);
}

/**
 * The frames at the clip's in and out points, decoded with mediabunny's
 * `CanvasSink` (independent of the preview `<video>`, so it never disturbs
 * playback). Regenerated on a debounce as the handles move; hides itself if the
 * browser can't decode the video.
 */
export function ClipFrameThumbs({
  file,
  start,
  end,
  duration,
}: {
  file: Blob;
  start: number;
  end: number;
  duration: number;
}) {
  const t = useTranslations("mediaTools.clipFrameThumbs");
  const [thumbs, setThumbs] = useState<{ start: string | null; end: string | null }>({
    start: null,
    end: null,
  });
  const [failed, setFailed] = useState(false);
  const thumbnailerRef = useRef<FrameThumbnailer | null>(null);

  // (Re)build the decode pipeline for the current file.
  useEffect(() => {
    let disposed = false;
    setThumbs({ start: null, end: null });
    setFailed(false);
    thumbnailerRef.current = null;
    createFrameThumbnailer(file, { width: THUMB_WIDTH }).then((thumbnailer) => {
      if (disposed) {
        thumbnailer?.dispose();
        return;
      }
      if (!thumbnailer) setFailed(true);
      else thumbnailerRef.current = thumbnailer;
    });
    return () => {
      disposed = true;
      thumbnailerRef.current?.dispose();
      thumbnailerRef.current = null;
    };
  }, [file]);

  // Debounced regeneration as the in/out points change.
  useEffect(() => {
    if (failed) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const thumbnailer = thumbnailerRef.current;
      if (!thumbnailer) return;
      try {
        const inAt = Math.max(0, Math.min(start, duration));
        const outAt = Math.max(0, Math.min(end, duration) - 0.001);
        const inFrame = await thumbnailer.frameAt(inAt);
        const outFrame = await thumbnailer.frameAt(outAt);
        if (cancelled) return;
        setThumbs({
          start: inFrame ? toDataUrl(inFrame) : null,
          end: outFrame ? toDataUrl(outFrame) : null,
        });
      } catch {
        if (!cancelled) setFailed(true);
      }
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [file, start, end, duration, failed]);

  if (failed) return null;

  return (
    <div className="flex gap-4">
      <Thumb label={t("inFrame")} time={start} src={thumbs.start} />
      <Thumb label={t("outFrame")} time={end} src={thumbs.end} />
    </div>
  );
}

function Thumb({ label, time, src }: { label: string; time: number; src: string | null }) {
  return (
    <div className="space-y-1">
      <div className="flex aspect-video w-40 items-center justify-center overflow-hidden rounded border bg-black">
        {src ? (
          <img src={src} alt={label} className="h-full w-full object-contain" />
        ) : (
          <span className="text-xs text-foreground/40">…</span>
        )}
      </div>
      <p className="text-xs text-foreground/60">
        {label} <span className="font-mono text-foreground/40">{formatClock(time)}</span>
      </p>
    </div>
  );
}
